import React, { ReactNode } from 'react'

import { useLocation } from 'react-router-dom'

import { User } from '@/core/api/entities/user'
import useRoutePlayerControl from '@/core/hooks/useRoutePlayerControl'
import GlobalPlayer from '@/shared/components/organisms/GlobalPlayer'
import Navbar from '@/shared/components/organisms/Layout/Navbar/NavBar'
import TopAppBar from '@/shared/components/organisms/Layout/TopAppBar/TopAppBar'
import ZoneStatus from '@/shared/components/organisms/Layout/TopAppBar/ZoneStatus'

import MobileDrawerMenu from '../MobileDrawerMenu/MobileDrawerMenu'

// Routes where the global player should persist even when navigating between them
// This allows music to continue playing when moving between related pages
const GLOBAL_PLAYER_PERSISTENT_ROUTES = [
	'/zones/:zoneId', // Specific zone details
	'/radios/:id', // Specific playlist details
	'/campaigns/:id', // Specific campaign details
	'/groups/:id', // Specific group details
	'/campaigns/:id/edit/upload', // Specific campaign edit upload
	'/campaigns/create/upload', // Specific campaign create upload
]

interface MainLayoutProps {
	children: ReactNode
	isAuthenticated?: boolean
	user?: User
	showTopAppBar?: boolean
	topAppBarContent?: ReactNode
	topAppBarLeftContent?: ReactNode
	className?: string
	hideSidebar?: boolean
}

const MainLayout: React.FC<MainLayoutProps> = ({
	children,
	isAuthenticated = false,
	user,
	showTopAppBar = true,
	topAppBarContent,
	topAppBarLeftContent,
	className,
	hideSidebar = false,
}) => {
	const location = useLocation()

	const hideTopAppBarOnMobile =
		/^\/(zones|groups|locations)\/\d+$/.test(location.pathname) ||
		location.pathname === '/integrations' ||
		location.pathname === '/account' ||
		/^\/campaigns\/\d+$/.test(location.pathname) ||
		/^\/campaigns\/create(\/upload)?$/.test(location.pathname) ||
		/^\/campaigns\/\d+\/edit(\/upload)?$/.test(location.pathname) ||
		/^\/schedules\/\d+$/.test(location.pathname) ||
		/^\/schedules\/new$/.test(location.pathname) ||
		location.pathname.startsWith('/campaigns/create-ai')
	// Manage the global player's visibility based on route changes
	// This is a global fallback that can be overridden by more specific implementations
	useRoutePlayerControl(GLOBAL_PLAYER_PERSISTENT_ROUTES)

	// Default middle content is the ZoneStatus component
	const defaultContent = <ZoneStatus />

	// Use custom middle content if provided, otherwise use the default
	const middleContent = topAppBarContent || defaultContent

	return (
		<div className={className + ' flex flex-col min-h-screen bg-background'}>
			{isAuthenticated && (
				<>
					{!hideSidebar && <Navbar />}
					{showTopAppBar && (
						<div className={hideTopAppBarOnMobile ? 'hidden md:block' : ''}>
							<TopAppBar
								userName={user?.getFullName() || ''}
								middleContent={middleContent}
								leftContent={topAppBarLeftContent}
								showLogo={hideSidebar}
							/>
						</div>
					)}
				</>
			)}
			<MainContent
				isAuthenticated={isAuthenticated}
				hasTopBar={isAuthenticated && showTopAppBar}
				hideSidebar={hideSidebar}
			>
				{children}
			</MainContent>
			{isAuthenticated && <GlobalPlayer />}
			<MobileDrawerMenu />
		</div>
	)
}

const MainContent = ({
	children,
	isAuthenticated,
	hideSidebar,
}: {
	children: ReactNode
	isAuthenticated: boolean
	hasTopBar: boolean
	hideSidebar: boolean
}) => {
	const paddingClass = !isAuthenticated || hideSidebar ? '' : 'lg:pl-20'

	return (
		<main className={`flex-1 transition-all duration-300 ${paddingClass}`}>
			{children}
		</main>
	)
}

export default MainLayout
